import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Utensils, Trash2, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

export function MealtimesDialog({ 
  userMealTimes, 
  setUserMealTimes,
  customMealName,
  setCustomMealName,
  savingMealTimes,
  setSavingMealTimes 
}) {
  const handleToggleMeal = (mealId) => {
    setUserMealTimes(prev => prev.map(m => 
      m.id === mealId ? { ...m, enabled: !m.enabled } : m
    ));
  };

  const handleDeleteCustomMeal = (mealId) => {
    setUserMealTimes(prev => prev.filter(m => m.id !== mealId));
  };

  const handleAddCustomMeal = () => {
    if (customMealName.trim()) {
      const newId = customMealName.toLowerCase().replace(/\s+/g, '_');
      if (!userMealTimes.find(m => m.id === newId)) {
        setUserMealTimes(prev => [...prev, {
          id: newId,
          name: customMealName.trim(),
          icon: '🍽️',
          enabled: true
        }]);
        setCustomMealName('');
      } else {
        toast.error('Meal time already exists');
      }
    }
  };

  const handleSaveMealTimes = async () => {
    setSavingMealTimes(true);
    try {
      const token = localStorage.getItem('gym_notebook_auth_token');
      if (token) {
        await api.put('/data/meal-times', { mealTimes: userMealTimes });
        toast.success('Meal times saved!');
      }
      localStorage.setItem('gym_notebook_meal_times', JSON.stringify(userMealTimes));
    } catch (error) {
      console.error('Error saving meal times:', error);
      toast.error('Failed to save meal times');
    }
    setSavingMealTimes(false);
  };

  const standardMeals = userMealTimes.filter(m => ['breakfast', 'lunch', 'dinner', 'snack'].includes(m.id));
  const customMeals = userMealTimes.filter(m => !['breakfast', 'lunch', 'dinner', 'snack'].includes(m.id));

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="flex flex-col items-center gap-2 p-4 bg-slate-800/50 rounded-2xl hover:bg-slate-800 transition-colors border border-slate-700/50">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Utensils className="h-5 w-5 text-white" />
          </div>
          <span className="text-sm text-white">Mealtimes</span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Utensils className="h-4 w-4 text-white" />
            </div>
            Configure Mealtimes
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <p className="text-slate-400 text-sm">
            Choose which meals you want to track. All nutrition features will use these meal times.
          </p>
          
          {/* Standard Meals */}
          <div className="space-y-2">
            <Label className="text-white text-sm">Standard Meals</Label>
            <div className="space-y-2">
              {standardMeals.map((meal) => (
                <div 
                  key={meal.id}
                  className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${
                    meal.enabled 
                      ? 'bg-slate-700/50 border-slate-600' 
                      : 'bg-slate-800/30 border-slate-700/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{meal.icon}</span>
                    <span className={meal.enabled ? 'text-white' : 'text-slate-500'}>{meal.name}</span>
                  </div>
                  <button
                    onClick={() => handleToggleMeal(meal.id)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      meal.enabled ? 'bg-green-500' : 'bg-slate-600'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      meal.enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          {/* Custom Meals */}
          <div className="space-y-2">
            <Label className="text-white text-sm">Custom Meals</Label>
            <div className="space-y-2">
              {customMeals.map((meal) => (
                <div 
                  key={meal.id}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-700/50 border border-slate-600"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{meal.icon}</span>
                    <span className="text-white">{meal.name}</span>
                  </div>
                  <button
                    onClick={() => handleDeleteCustomMeal(meal.id)}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            
            {/* Add Custom Meal */}
            <div className="flex gap-2 pt-2">
              <Input
                value={customMealName}
                onChange={(e) => setCustomMealName(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white flex-1"
                placeholder="e.g., Snack 2, Pre-workout, etc."
              />
              <Button
                onClick={handleAddCustomMeal}
                className="bg-amber-500 hover:bg-amber-400"
                disabled={!customMealName.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Save Button */}
          <Button
            onClick={handleSaveMealTimes}
            disabled={savingMealTimes}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400"
          >
            {savingMealTimes ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Mealtimes
          </Button>
          
          <p className="text-xs text-slate-500 text-center">
            These meal times will be used across all nutrition features
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MealtimesDialog;
